export const COMPLETE_LIST_PAGE_SIZE = 100

const PAGE_FETCH_CONCURRENCY = 4

interface PaginationMeta {
  page: number
  perPage: number
  total: number
}

interface PaginatedResponse {
  code: number
  msg?: string
  pagination: PaginationMeta
}

interface FetchAllPagesOptions<T, R extends PaginatedResponse> {
  fetchPage: (page: number, pageSize: number) => Promise<R>
  getItems: (response: R) => readonly T[]
  pageSize?: number
}

function assertSuccessfulResponse(response: PaginatedResponse, page: number) {
  if (response.code !== 0) {
    throw new Error(response.msg || `Failed to fetch page ${page}`)
  }
}

/**
 * Fetches every page reported by a paginated API.
 *
 * Results are only returned when every expected page succeeds and the number
 * of received records covers the API's reported total. This prevents callers
 * from accidentally presenting a partial collection as a complete one.
 */
export async function fetchAllPages<T, R extends PaginatedResponse>({
  fetchPage,
  getItems,
  pageSize = COMPLETE_LIST_PAGE_SIZE,
}: FetchAllPagesOptions<T, R>): Promise<T[]> {
  const firstResponse = await fetchPage(1, pageSize)
  assertSuccessfulResponse(firstResponse, 1)

  const firstItems = [...getItems(firstResponse)]
  const total = Math.max(firstResponse.pagination.total, firstItems.length)
  const effectivePageSize = firstResponse.pagination.perPage || pageSize
  const pageCount = Math.ceil(total / effectivePageSize)

  if (pageCount <= 1) return firstItems

  const remainingPages = Array.from({ length: pageCount - 1 }, (_, index) => index + 2)
  const itemsByPage: T[][] = [firstItems]

  for (let index = 0; index < remainingPages.length; index += PAGE_FETCH_CONCURRENCY) {
    const pageBatch = remainingPages.slice(index, index + PAGE_FETCH_CONCURRENCY)
    const responses = await Promise.all(
      pageBatch.map(async (page) => {
        const response = await fetchPage(page, pageSize)
        assertSuccessfulResponse(response, page)
        return [...getItems(response)]
      }),
    )
    itemsByPage.push(...responses)
  }

  const items = itemsByPage.flat()
  if (items.length < total) {
    throw new Error(
      `Incomplete paginated response: expected ${total} records, received ${items.length}`,
    )
  }

  return items
}
